import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Save, ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  fetchReminderTemplate,
  listWatiTemplates,
  saveReminderTemplate,
  testSendReminder,
} from "../../lib/adminApi";

/**
 * Admin → Reminder Templates.
 *
 * Configures the single "policy-premium reminder" template used by the daily 5 AM cron
 * (see `backend/app/routes_reminders.py`). Two channels:
 *   - WhatsApp: pick a pre-approved WATI template + map its `{{1..N}}` placeholders to
 *     variables the cron will populate from each policy/member row.
 *   - Email: subject + HTML body with `{{variable}}` placeholders (same variable names as
 *     the WATI map). Free-form HTML so admins can bring the brand in.
 *
 * A "Test send" button dispatches the currently-saved template to any email + WA number
 * with either the canned sample variables or the admin's overrides.
 */
export function AdminReminderTemplatesPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [tpl, setTpl] = useState(null);
  const [watiTemplates, setWatiTemplates] = useState([]);
  const [testTo, setTestTo] = useState({ email: "", whatsapp: "" });
  const [lastTestResult, setLastTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [saved, wa] = await Promise.all([
        fetchReminderTemplate(),
        listWatiTemplates().catch(() => ({ templates: [] })),
      ]);
      setTpl({
        wa_template_id: saved?.wa_template_id || "",
        wa_variable_map: saved?.wa_variable_map || [],
        email_subject: saved?.email_subject || "",
        email_body_html: saved?.email_body_html || "",
        allowed_variables: saved?.allowed_variables || [],
        updated_at: saved?.updated_at,
        updated_by: saved?.updated_by,
      });
      setWatiTemplates(wa?.templates || wa || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load the reminder template.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (key) => (e) => setTpl((prev) => ({ ...prev, [key]: e.target.value }));

  const onVariableAt = (idx) => (e) => {
    setTpl((prev) => {
      const next = [...(prev.wa_variable_map || [])];
      next[idx] = e.target.value;
      return { ...prev, wa_variable_map: next };
    });
  };

  const setVariableCount = (count) => {
    setTpl((prev) => {
      const cur = prev.wa_variable_map || [];
      const next = Array.from({ length: count }, (_, i) => cur[i] || "");
      return { ...prev, wa_variable_map: next };
    });
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await saveReminderTemplate({
        wa_template_id: tpl.wa_template_id || null,
        wa_variable_map: (tpl.wa_variable_map || []).filter(Boolean),
        email_subject: tpl.email_subject || "",
        email_body_html: tpl.email_body_html || "",
      });
      toast.success("Template saved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onTestSend = async () => {
    if (!testTo.email && !testTo.whatsapp) {
      toast.error("Enter at least one test recipient (email or WhatsApp).");
      return;
    }
    setTesting(true);
    setLastTestResult(null);
    try {
      const res = await testSendReminder({
        to_email: testTo.email || null,
        to_whatsapp: testTo.whatsapp || null,
      });
      setLastTestResult(res);
      toast.success("Test send dispatched — see results below.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading || !tpl) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="flex items-center gap-2 text-sm text-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading template…
        </div>
      </div>
    );
  }

  const varCount = (tpl.wa_variable_map || []).length;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8" data-testid="admin-reminder-templates-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-muted hover:text-gold" data-testid="page-back">
        <ChevronLeft className="h-3 w-3" /> Admin
      </Link>
      <h1 className="mt-2 font-serif text-3xl text-text sm:text-4xl">
        Reminder <span className="text-gold">Templates</span>
      </h1>
      <p className="mt-1 text-sm text-muted">
        Used by the daily 5 AM cron to nudge members before every policy renewal. Configure
        WATI + email once — the cron dispatches automatically.
      </p>

      {/* WATI section */}
      <section className="mt-8 rounded-lg border border-border bg-card p-6">
        <h2 className="text-lg font-semibold text-text">WhatsApp (WATI)</h2>
        <p className="mt-1 text-xs text-muted">
          Pick one of your approved WATI templates and map each `{"{{1..N}}"}` placeholder to
          a variable name the cron will substitute from the policy + member row.
        </p>
        <div className="mt-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>Template</Label>
            <select
              value={tpl.wa_template_id || ""}
              onChange={set("wa_template_id")}
              className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-text"
              data-testid="wa-template-select"
            >
              <option value="">— select —</option>
              {watiTemplates.map((t) => (
                <option key={t.id || t.elementName || t.name} value={t.elementName || t.name || t.id}>
                  {(t.elementName || t.name || t.id)}{t.status ? ` · ${t.status}` : ""}
                </option>
              ))}
            </select>
            {watiTemplates.length === 0 && (
              <p className="text-[11px] text-amber-400">
                No WATI templates loaded. Refresh them from Admin → Integrations → WATI.
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Label>Number of placeholders</Label>
            <Input
              type="number"
              min="0"
              max="10"
              value={varCount}
              onChange={(e) => setVariableCount(Math.max(0, Math.min(10, Number(e.target.value) || 0)))}
              className="w-20"
              data-testid="wa-var-count"
            />
          </div>

          {varCount > 0 && (
            <div className="grid gap-2 sm:grid-cols-2">
              {(tpl.wa_variable_map || []).map((v, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="w-14 shrink-0 rounded bg-elevated px-2 py-1 text-center font-mono text-xs text-muted">
                    {`{{${idx + 1}}}`}
                  </span>
                  <select
                    value={v}
                    onChange={onVariableAt(idx)}
                    className="flex-1 rounded-md border border-border bg-bg px-2 py-1 text-sm text-text"
                    data-testid={`wa-var-${idx}`}
                  >
                    <option value="">— pick a variable —</option>
                    {tpl.allowed_variables.map((av) => (
                      <option key={av} value={av}>{av}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Email section */}
      <section className="mt-6 rounded-lg border border-border bg-card p-6">
        <h2 className="text-lg font-semibold text-text">Email (branded HTML)</h2>
        <p className="mt-1 text-xs text-muted">
          Use `{"{{variable_name}}"}` anywhere in subject / body — same variable names as the WATI
          map. Common ones: <code>{"{{member_name}} · {{policy_name}} · {{insurer}} · {{premium_amount}} · {{due_date}} · {{days_until_due}} · {{product_name}}"}</code>
        </p>
        <div className="mt-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>Subject</Label>
            <Input
              value={tpl.email_subject}
              onChange={set("email_subject")}
              placeholder="e.g. Your {{policy_name}} premium is due in {{days_until_due}} days"
              data-testid="email-subject"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Body (HTML allowed)</Label>
            <textarea
              rows={10}
              value={tpl.email_body_html}
              onChange={set("email_body_html")}
              className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-text font-mono"
              placeholder={`<p>Hi {{member_name}},</p>\n<p>Your <strong>{{policy_name}}</strong> ({{insurer}}) premium of ₹{{premium_amount}} is due on {{due_date}} ({{days_until_due}} days from today).</p>\n<p>— {{product_name}}</p>`}
              data-testid="email-body"
            />
          </div>
        </div>
      </section>

      <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
        {tpl.updated_at && (
          <span className="mr-auto text-xs text-muted">
            Last saved: {new Date(tpl.updated_at).toLocaleString("en-IN")}
            {tpl.updated_by ? ` by ${tpl.updated_by}` : ""}
          </span>
        )}
        <Button onClick={onSave} disabled={saving} data-testid="save-template" className="gap-1.5">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save template
        </Button>
      </div>

      {/* Test send */}
      <section className="mt-8 rounded-lg border border-gold/30 bg-gold/5 p-6" data-testid="reminder-test-panel">
        <h2 className="text-lg font-semibold text-text">Test send</h2>
        <p className="mt-1 text-xs text-muted">
          Dispatches the CURRENTLY-SAVED template to the recipients below using sample data
          (Rahul Sharma / HDFC Life / ₹18,500). Save your changes first, then test.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label>To email</Label>
            <Input
              value={testTo.email}
              onChange={(e) => setTestTo((s) => ({ ...s, email: e.target.value }))}
              placeholder="test@example.com"
              data-testid="test-email"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>To WhatsApp (E.164)</Label>
            <Input
              value={testTo.whatsapp}
              onChange={(e) => setTestTo((s) => ({ ...s, whatsapp: e.target.value }))}
              placeholder="+919999999999"
              data-testid="test-whatsapp"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={onTestSend} disabled={testing} data-testid="test-send-btn" className="gap-1.5">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send test
          </Button>
        </div>
        {lastTestResult && (
          <pre
            className="mt-4 max-h-64 overflow-auto rounded-md border border-border bg-bg p-3 text-[11px] text-muted"
            data-testid="test-send-result"
          >
            {JSON.stringify(lastTestResult, null, 2)}
          </pre>
        )}
      </section>
    </div>
  );
}

export default AdminReminderTemplatesPage;
